import { useCallback, useRef, useState } from "react";
import * as ReactDOM from "react-dom";
import * as _ from "lodash";

/**
 *
 * Custom hook to know when overflow container reaches bottom of a page
 *
 * @returns
 *    forwardRef: To be put on the scrolling element (eg: <Page.Body ref={forwardRef}/>)
 *    loading: true the loadMoreDate function is being called
 *
 * @param triggerPercent: percentage for when request data is triggered in scroll;
 * @param loadMoreData: Promise called on first render + when scroll pane is at bottom.
 *                      Returning length of ids fetched.
 */

const useInfiniteScroll = (
  triggerPercent: number,
  loadMoreData: () => Promise<number>
) => {
  const [runOnStart, setRunOnStart] = useState(true);
  const [requestData, setRequestData] = useState(true);
  const ref = useRef(null);
  const calculateScrollEnd = () => {
    const DOMElement = ReactDOM.findDOMNode(ref.current);
    if (DOMElement != null) {
      if ("scrollHeight" in DOMElement && "scrollTop" in DOMElement) {
        const maxHeight = DOMElement.scrollHeight - DOMElement.clientHeight;
        const currentHeight = DOMElement.scrollTop;
        const atEnd = currentHeight / maxHeight > triggerPercent;
        if (!runOnStart && atEnd && requestData) {
          setRequestData(false);
          loadMoreData()
            .then((idsLoaded) => {
              setRequestData(idsLoaded !== 0);
            })
            .catch((e) => {
              setRequestData(false);
              throw e;
            });
        }
      }
    }
  };
  const debounceCalculate = _.debounce(calculateScrollEnd, 500);
  const setRef = useCallback(
    (node) => {
      if (ref.current) {
        // Cleanup old events
        const domNode = ReactDOM.findDOMNode(ref.current);
        if (!!domNode) {
          domNode.removeEventListener("scroll", debounceCalculate);
        }
      }

      //New node add events
      if (node) {
        const domNode = ReactDOM.findDOMNode(node);
        if (!!domNode) {
          domNode.addEventListener("scroll", debounceCalculate);
        }
      }

      ref.current = node;
    },
    [debounceCalculate]
  );

  if (runOnStart && requestData) {
    setRequestData(false);
    setRunOnStart(false);
    loadMoreData().then(() => {
      setRequestData(true);
    });
  }

  return { forwardRef: setRef, loading: !requestData };
};

export default useInfiniteScroll;
